/**
 * useVendor — MS26 per-vendor landing page data source.
 *
 * Looks up a vendor_partner by their derived slug + hydrates their
 * vendor_products in a single round trip. Public (anon-callable)
 * so the /vendors/<slug> page works for unauthed visitors and the
 * prerender script can fetch the data at build time.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { vendorSlug } from "@/lib/vendorSlug";

export interface VendorPartnerRow {
  id: string;
  vendor_name: string;
  website: string | null;
  instagram: string | null;
  delivery_suburbs: string | null;
  product_types: string[] | null;
  dietary_options: string[] | null;
}

export interface VendorProductCard {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  slug: string;
  vendor_category: string;
  is_voucher: boolean;
  variant_label: string | null;
  lead_time_days: number | null;
  // MS318: dietary_tags for OrderConfirmSheet recipient-conflict warning.
  dietary_tags: string[] | null;
}

export interface VendorPageData {
  vendor: VendorPartnerRow;
  products: VendorProductCard[];
}

/**
 * Resolve a vendor by slug. Returns null when no match (handled
 * as a 404-style fallback at the page level).
 *
 * Implementation note: we fetch ALL active vendor_partners + filter
 * client-side by slug. The list is small (~22 rows) and Supabase
 * doesn't have a slug column we could filter server-side. Caching
 * is aggressive (1 hour staleTime) since vendor names rarely change.
 */
export function useVendor(slug: string) {
  return useQuery({
    queryKey: ["vendor", slug],
    staleTime: 60 * 60 * 1000, // 1 hour
    queryFn: async (): Promise<VendorPageData | null> => {
      // Fetch all active vendor_partners and find the one whose
      // derived slug matches. Cheap because the list is small.
      const { data: vendors, error: vendorErr } = await supabase
        .from("vendor_partners")
        .select(
          "id, vendor_name, website, instagram, delivery_suburbs, product_types, dietary_options",
        )
        .eq("is_active", true);
      if (vendorErr) throw vendorErr;

      const vendor = (vendors ?? []).find(
        (v) => vendorSlug(v.vendor_name) === slug,
      );
      if (!vendor) return null;

      // Fetch their products in one go.
      const { data: products, error: prodErr } = await supabase
        .from("vendor_products")
        // MS318: dietary_tags select for the OrderConfirmSheet
        // recipient-conflict warning.
        .select(
          "id, name, description, price, image_url, slug, vendor_category, is_voucher, variant_label, lead_time_days, dietary_tags",
        )
        .eq("vendor_partner_id", vendor.id)
        .is("deleted_at", null)
        .eq("is_available", true)
        // MS492: one card per product family (sizes + voucher tiers).
        .is("parent_product_id", null)
        .order("sort_order", { ascending: true, nullsFirst: false })
        .order("name", { ascending: true });
      if (prodErr) throw prodErr;

      return {
        vendor: vendor as VendorPartnerRow,
        products: (products ?? []).map((p) => ({
          ...p,
          price: Number(p.price ?? 0),
        })) as VendorProductCard[],
      };
    },
  });
}

/**
 * Fetch all active vendor slugs. Used by the prerender script
 * at build time + by future link surfaces (e.g. a /vendors index
 * page). Not wrapped in useQuery because it's typically called
 * once at build, not on-mount.
 */
export async function fetchAllVendorSlugs(): Promise<string[]> {
  const { data, error } = await supabase
    .from("vendor_partners")
    .select("vendor_name")
    .eq("is_active", true);
  if (error) throw error;
  return (data ?? []).map((v) => vendorSlug(v.vendor_name));
}

/* ─── /vendors directory index — MS29 ────────────────────────────── */

export interface VendorIndexCard {
  slug: string;
  vendor_name: string;
  delivery_suburbs: string | null;
  primary_category: string;
  product_count: number;
  /** Primary product image to use as the card photo when Lucky's
   *  Notion-curated set isn't available for this vendor. */
  hero_image: string | null;
}

/**
 * Fetch all active vendors + their primary category + product count
 * + a hero image. Used by /vendors directory index. Single query
 * with a join + group-by, then post-processed client-side.
 */
export function useVendorIndex() {
  return useQuery({
    queryKey: ["vendor-index"],
    staleTime: 60 * 60 * 1000, // 1 hour — vendor list moves slowly
    queryFn: async (): Promise<VendorIndexCard[]> => {
      const { data: vendors, error: vErr } = await supabase
        .from("vendor_partners")
        .select("id, vendor_name, delivery_suburbs")
        .eq("is_active", true);
      if (vErr) throw vErr;
      if (!vendors || vendors.length === 0) return [];

      // Pull the products for all of them in a single round trip,
      // then group client-side.
      const { data: products, error: pErr } = await supabase
        .from("vendor_products")
        .select("vendor_partner_id, vendor_category, image_url, sort_order")
        .is("deleted_at", null)
        .eq("is_available", true);
      if (pErr) throw pErr;

      const byVendor = new Map<
        string,
        { count: number; categories: string[]; firstImage: string | null }
      >();
      for (const p of products ?? []) {
        const id = (p as { vendor_partner_id: string }).vendor_partner_id;
        const entry = byVendor.get(id) ?? { count: 0, categories: [], firstImage: null };
        entry.count += 1;
        const cat = (p as { vendor_category: string }).vendor_category;
        if (cat && !entry.categories.includes(cat)) entry.categories.push(cat);
        if (!entry.firstImage) {
          const url = (p as { image_url: string | null }).image_url;
          if (url) entry.firstImage = url;
        }
        byVendor.set(id, entry);
      }

      return vendors
        .map((v): VendorIndexCard => {
          const entry = byVendor.get(v.id) ?? { count: 0, categories: [], firstImage: null };
          return {
            slug: vendorSlug(v.vendor_name),
            vendor_name: v.vendor_name,
            delivery_suburbs: v.delivery_suburbs,
            primary_category: entry.categories[0] ?? "voucher",
            product_count: entry.count,
            hero_image: entry.firstImage,
          };
        })
        // MS296: catalogue-honesty parity with MS283/285/293. A vendor
        // whose entire SKU set is `is_available=false` (e.g. Chéri,
        // paused 2026-05) was rendering as an empty card here with
        // `product_count: 0` + a fallback `primary_category: "voucher"`
        // that wasn't backed by any real SKU. Visitors clicked through,
        // landed on /vendors/<slug>, and saw an empty product grid.
        // The vendor_partners row stays `is_active=true` (we want them
        // back), but the directory should only surface vendors a
        // visitor can actually order from today.
        .filter((card) => card.product_count > 0)
        .sort((a, b) => a.vendor_name.localeCompare(b.vendor_name));
    },
  });
}
